import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { SocialProviderId } from '@/lib/auth';

interface ProvidersResponse {
  /** Configured by the platform AND switched on by this tenant. Render these. */
  providers: SocialProviderId[];
  /** Everything the platform has credentials for — used by the admin UI. */
  configured: SocialProviderId[];
}

/**
 * Which social login buttons this tenant should show.
 *
 * The backend returns the intersection of "the platform has credentials" and
 * "the tenant enabled it", so a button can never appear for a provider that
 * would fail on click — which is exactly what used to happen with Apple and
 * Facebook on every tenant.
 *
 * Returns an empty list while loading so nothing flashes in and back out.
 * If the request fails (e.g. a frontend deployed ahead of its backend during a
 * rollout) the list stays empty and email/password login still works.
 */
export function useSocialProviders() {
  const query = useQuery({
    queryKey: ['social-providers'],
    queryFn: () => api.get<ProvidersResponse>('/auth/providers'),
    staleTime: 1000 * 60 * 5,
  });

  return {
    providers: query.data?.providers ?? [],
    configured: query.data?.configured ?? [],
    isLoading: query.isLoading,
  };
}
